<template>
  <div class="monitor container is-fluid">
    <div class="columns">
      <div class="column is-8">
        <h4 class="is-size-4-desktop">Monitor de pedidos</h4>
        <h4 v-if="bodega" class="is-size-6-desktop">
          Sucursal: {{ bodega.sucursal.nombre }} - {{ bodega.nombre }}
        </h4>
        <div v-else class="notification is-info is light">
          El usuario identificado no se encuentra asignado a una sucursal y/o
          bodega. Favor contactar al administrador.
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column is-12 has-text-centered">
        <h3 class="is-size-4 has-text-weight-bold">Pedidos de cliente</h3>
        <b-table
          :data="pedidos"
          :paginated="true"
          per-page="10"
          :striped="true"
          :current-page="paginaActual"
          :pagination-simple="false"
          @page-change="pageChangeHandle"
        >
          <b-table-column v-slot="props" field="orden" label="#" width="3%">
            <p class="is-size-5 has-text-weight-medium">
              {{ props.row.orden }}
            </p>
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="cliente"
            label="Cliente"
            width="30%"
            :centered="true"
          >
            <p class="is-size-5 has-text-weight-medium">
              {{ props.row.cliente }}
            </p>
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="numentoDocumento"
            label="N° Documento"
            :centered="true"
            width="14%"
          >
            <p class="is-size-5 has-text-weight-medium">
              {{ props.row.tipoVenta }}
              {{
                props.row.numeroDocumento
                  ? ` / ${props.row.numeroDocumento}`
                  : ''
              }}
            </p>
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="estado"
            label="Estado"
            :centered="true"
            width="10%"
          >
            <p
              v-if="props.row.estado == 'En Despacho'"
              class="tag is-success is-size-5 has-text-weight-medium"
            >
              {{ props.row.estado }}
            </p>
            <p v-else class="is-size-5 has-text-weight-medium">
              {{ props.row.estado }}
            </p>
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="fecha"
            label="Hora Facturación"
            :centered="true"
            width="8%"
          >
            <p class="is-size-5 has-text-weight-medium">
              {{ format(parseISO(props.row.fecha), 'HH:mm') }}
            </p>
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="encargados"
            label="Encargado(s)"
            width="25%"
            :centered="true"
          >
            <p class="is-size-5 has-text-weight-medium">
              {{ props.row.encargados }}
            </p>
          </b-table-column>
          <template slot="empty">
            <b-notification
              type="is-info is-light"
              :has-icon="true"
              :closable="false"
            >
              No hay pedidos pendientes de preparaci&oacute;n y/o entrega
            </b-notification>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as signalR from '@microsoft/signalr'
import { parseISO, format } from 'date-fns'

const PAGE_ITEMS = 10
const CHANGE_TIME = 10000

export default {
  name: 'Monitorv2',
  data() {
    return {
      intervalo: null,
      paginaActual: 1,
      connection: '',
      reconectando: false,
      monitorHeight: 0,
      parseISO,
      format
    }
  },
  computed: {
    ...mapGetters('sesion', ['bodega']),
    ...mapGetters('monitorCliente', ['pedidos']),
    mostrarPedidos() {
      return this.bodega
    },
    numeroPaginas() {
      return Math.ceil(this.pedidos.length / PAGE_ITEMS)
    }
  },
  watch: {
    bodega() {
      this.enableHub()
    }
  },
  created() {
    this.createHub()
  },
  mounted() {
    const self = this
    self.intervalo = setInterval(() => self.nextPage(), CHANGE_TIME)
  },
  beforeDestroy() {
    this.stopHub()
    clearInterval(this.intervalo)
  },
  methods: {
    createHub() {
      const self = this
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${process.env.VUE_APP_BASE_URL}hubs/pedidos`, {
          accessTokenFactory: () => localStorage.getItem('token') || ''
        })
        .withAutomaticReconnect([5000, 5000, 10000, 10000, 10000, 10000, 10000])
        .configureLogging(signalR.LogLevel.Information)
        .build()
      this.connection.onreconnecting(() => (self.reconectando = true))
      this.connection.onreconnected(() => (self.reconectando = false))
      this.connection.onclose(() => {
        if (self.connection.state === 'Disconnected' && self.reconectando) {
          self.notificarError(
            'Se perdió la conexión con el servidor. Se volverá a intentar luego.'
          )
          setTimeout(() => {
            self.startHub()
          }, 30000)
        }
      })
      this.startHub()
      this.enableHub()
    },
    startHub() {
      const self = this
      return this.connection
        .start()
        .then((_) => {
          self.connection
            .invoke('conectar-bodega', `${self.bodega.id}`)
            .catch((err) => self.notificarError(err.toString()))
          self.$nextTick(() => self.getPedidos())
        })
        .catch((err) => {
          const error = Object.assign({}, err)
          if (error.statusCode === 401) {
            const returnUrl = self.$route.fullPath
            self.$router.push(`/login?returnUrl=${returnUrl}`).catch(() => {})
          } else {
            self.procesarError(err)
          }
        })
    },
    stopHub() {
      const self = this
      this.connection
        .invoke('desconectar-bodega', `${self.bodega.id}`)
        .catch((err) => self.notificarError(err.toString()))
        .then((_) => self.connection.stop())
    },
    enableHub() {
      const self = this
      self.connection.on('UpdatePedidos', () =>
        self.$nextTick(() => self.getPedidos())
      )
    },
    getPedidos() {
      const self = this
      return Promise.all([
        self.$store.dispatch('monitorCliente/updatePedidos')
      ]).catch((error) => this.procesarError(error))
    },
    nextPage() {
      if (this.paginaActual + 1 > this.numeroPaginas) this.paginaActual = 1
      else this.paginaActual++
    },
    pageChangeHandle(page) {
      this.paginaActual = page
    }
  }
}
</script>
